import "./App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { MobileProtectedRoute } from "./components/MobileProtectedRoute";
import { AdminRoute, UserMgmtRoute, SampleRoute, CiAdminRoute } from "./components/AdminRoute";
import SamplesPage from "./pages/SamplesPage";
import CiReservationsPage from "./pages/CiReservationsPage";
import MobileSampleReceive from "./mobile/MobileSampleReceive";
import { Toaster } from "./components/ui/sonner";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Settings from "./pages/Settings";
import UserManagementPage from "./pages/UserManagementPage";
import ApiTokensPage from "./pages/ApiTokensPage";
import CartPage from "./pages/CartPage";
import CustomersPage from "./pages/CustomersPage";
import QuoteCalculatorPage from "./pages/QuoteCalculatorPage";
import ProductShareLinkPage from "./pages/ProductShareLinkPage";
import CustomerQuoteLinkPage from "./pages/CustomerQuoteLinkPage";
import SalesRepHomePage from "./pages/SalesRepHomePage";
import MobileLogin from "./mobile/MobileLogin";
import MobileShell from "./mobile/MobileShell";
import MobileCatalog from "./mobile/MobileCatalog";
import MobileQuotesList from "./mobile/MobileQuotesList";
import MobileQuoteDetail from "./mobile/MobileQuoteDetail";
import MobileProfile from "./mobile/MobileProfile";

// The mobile app (/m/*) has a fixed bottom tab bar, so its toasts need to
// float above that bar (top-center) instead of the desktop's bottom-right -
// otherwise a toast can visually overlap and block clicks on the tab bar.
function AppToaster() {
  const location = useLocation();
  const isMobile = location.pathname.startsWith("/m");
  return <Toaster position={isMobile ? "top-center" : "bottom-right"} />;
}

// The mobile (/m/*) and desktop apps are installable as separate PWAs from
// the same domain, so they each need their own manifest (different
// start_url/scope/name). Browsers only read the manifest link present in
// the DOM, so we swap its href whenever the route crosses that boundary.
function PwaManifestSwitcher() {
  const location = useLocation();
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    link.setAttribute(
      "href",
      location.pathname.startsWith("/m") ? "/manifest.json" : "/manifest-desktop.json",
    );
  }, [location.pathname]);
  return null;
}

// Matches phone browsers only (not tablets/desktop) so iPad/Android-tablet
// visitors keep the desktop layout, which fits their larger screens fine.
const MOBILE_UA_REGEX = /iPhone|iPod|Android.*Mobile|Windows Phone/i;
const isMobileDevice = () => MOBILE_UA_REGEX.test(window.navigator.userAgent);

function mobileEquivalentPath(pathname) {
  if (pathname === "/login") return "/m/login";
  if (pathname === "/cart") return "/m/quotes";
  return "/m/catalog";
}

// Visiting the desktop app's URLs on a phone browser should land the user
// straight in the mobile experience (/m/*) instead of the desktop UI. The
// public, device-agnostic /quote/:token quote-calculator page is deliberately
// excluded - it's shared as-is on any device.
function MobileDeviceGate({ children }) {
  const { pathname } = useLocation();
  if (isMobileDevice() && !pathname.startsWith("/m") && !pathname.startsWith("/quote/")) {
    return <Navigate to={mobileEquivalentPath(pathname)} replace />;
  }
  return children;
}

function App() {
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/service-worker.js").catch(() => {});
    }
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <PwaManifestSwitcher />
            <MobileDeviceGate>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/quote/:token" element={<QuoteCalculatorPage />} />
                <Route path="/share/products/:token" element={<ProductShareLinkPage />} />
                <Route path="/quote/customer/:token" element={<CustomerQuoteLinkPage />} />
                <Route path="/sales-rep-home" element={<SalesRepHomePage />} />
                <Route path="/m/login" element={<MobileLogin />} />
                <Route
                  path="/m"
                  element={
                    <MobileProtectedRoute>
                      <MobileShell />
                    </MobileProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="catalog" replace />} />
                  <Route path="home" element={<Navigate to="/m/catalog" replace />} />
                  <Route path="catalog" element={<MobileCatalog />} />
                  <Route path="quotes" element={<MobileQuotesList />} />
                  <Route path="quotes/:id" element={<MobileQuoteDetail />} />
                  <Route path="samples" element={<MobileSampleReceive />} />
                  <Route path="profile" element={<MobileProfile />} />
                </Route>
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cart"
                  element={
                    <ProtectedRoute>
                      <CartPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers"
                  element={
                    <ProtectedRoute>
                      <CustomersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/samples"
                  element={
                    <SampleRoute>
                      <SamplesPage />
                    </SampleRoute>
                  }
                />
                <Route
                  path="/ci-reservations"
                  element={
                    <CiAdminRoute>
                      <CiReservationsPage />
                    </CiAdminRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <AdminRoute>
                      <Settings />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <UserMgmtRoute>
                      <UserManagementPage />
                    </UserMgmtRoute>
                  }
                />
                <Route
                  path="/access-tokens"
                  element={
                    <AdminRoute>
                      <ApiTokensPage />
                    </AdminRoute>
                  }
                />
              </Routes>
            </MobileDeviceGate>
            <AppToaster />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;

